import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, CheckCircle2, Clock, Target,
  Sparkles, TrendingUp, Award, ChevronDown,
  Zap, Heart, Users, Leaf, Brain
} from 'lucide-react';

// Activity categories aligned with values and wellbeing
const ACTIVITY_CATEGORIES = {
  mastery: {
    icon: <Target className="w-4 h-4" />,
    label: 'Mastery & Achievement',
    colour: 'bg-blue-500/20 border-blue-500',
    description: 'Activities that build competence and accomplishment',
    examples: ['Learning something new', 'Completing a task', 'Solving a problem', 'Developing a skill']
  },
  pleasure: {
    icon: <Heart className="w-4 h-4" />,
    label: 'Pleasure & Enjoyment',
    colour: 'bg-pink-500/20 border-pink-500',
    description: 'Activities that bring joy and positive emotions',
    examples: ['Hobbies', 'Entertainment', 'Treats', 'Fun activities']
  },
  connection: {
    icon: <Users className="w-4 h-4" />,
    label: 'Connection & Relationships',
    colour: 'bg-purple-500/20 border-purple-500',
    description: 'Activities involving others and social engagement',
    examples: ['Calling a friend', 'Family time', 'Group activities', 'Helping others']
  },
  selfCare: {
    icon: <Leaf className="w-4 h-4" />,
    label: 'Self-Care & Health',
    colour: 'bg-green-500/20 border-green-500',
    description: 'Activities that nurture your body and mind',
    examples: ['Exercise', 'Rest', 'Healthy eating', 'Nature time']
  },
  meaning: {
    icon: <Sparkles className="w-4 h-4" />,
    label: 'Meaning & Purpose',
    colour: 'bg-amber-500/20 border-amber-500',
    description: 'Activities aligned with your values and purpose',
    examples: ['Volunteering', 'Creative work', 'Spiritual practice', 'Contributing']
  }
};

// Pre-defined activity suggestions
const ACTIVITY_SUGGESTIONS = [
  { name: 'Go for a 10-minute walk', category: 'selfCare', difficulty: 'easy', mood_boost: 3 },
  { name: 'Call a friend or family member', category: 'connection', difficulty: 'medium', mood_boost: 4 },
  { name: 'Complete one small task', category: 'mastery', difficulty: 'easy', mood_boost: 3 },
  { name: 'Listen to favourite music', category: 'pleasure', difficulty: 'easy', mood_boost: 3 },
  { name: 'Tidy one small area', category: 'mastery', difficulty: 'easy', mood_boost: 2 },
  { name: 'Practice 5 minutes of breathing', category: 'selfCare', difficulty: 'easy', mood_boost: 2 },
  { name: 'Write in a journal', category: 'meaning', difficulty: 'medium', mood_boost: 3 },
  { name: 'Cook a healthy meal', category: 'selfCare', difficulty: 'medium', mood_boost: 4 },
  { name: 'Do something creative', category: 'pleasure', difficulty: 'medium', mood_boost: 4 },
  { name: 'Help someone with something', category: 'connection', difficulty: 'medium', mood_boost: 5 },
  { name: 'Learn something new for 15 mins', category: 'mastery', difficulty: 'medium', mood_boost: 3 },
  { name: 'Spend time in nature', category: 'selfCare', difficulty: 'medium', mood_boost: 4 }
];

interface Activity {
  id: string;
  name: string;
  category: keyof typeof ACTIVITY_CATEGORIES;
  scheduledFor?: string;
  completed: boolean;
  completedAt?: string;
  moodBefore?: number;
  moodAfter?: number;
  notes?: string;
  difficulty: 'easy' | 'medium' | 'challenging';
}

interface BehaviouralActivationProps {
  onActivityComplete?: (activity: Activity) => void;
  onScheduleActivity?: (activity: Activity) => void;
}

export function BehaviouralActivation({ 
  onActivityComplete,
  onScheduleActivity 
}: BehaviouralActivationProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [newActivity, setNewActivity] = useState({
    name: '',
    category: 'selfCare' as keyof typeof ACTIVITY_CATEGORIES,
    scheduledFor: '',
    difficulty: 'easy' as 'easy' | 'medium' | 'challenging'
  });
  const [completingActivity, setCompletingActivity] = useState<string | null>(null);
  const [completionData, setCompletionData] = useState({
    moodBefore: 5,
    moodAfter: 5,
    notes: ''
  });

  const addActivity = () => {
    if (!newActivity.name.trim()) return;
    
    const activity: Activity = {
      id: Date.now().toString(),
      name: newActivity.name,
      category: newActivity.category,
      scheduledFor: newActivity.scheduledFor || undefined,
      completed: false,
      difficulty: newActivity.difficulty
    };
    
    setActivities(prev => [...prev, activity]);
    onScheduleActivity?.(activity);
    setNewActivity({ name: '', category: 'selfCare', scheduledFor: '', difficulty: 'easy' });
    setShowAddForm(false);
  };

  const addSuggestedActivity = (suggestion: typeof ACTIVITY_SUGGESTIONS[0]) => {
    const activity: Activity = {
      id: Date.now().toString(),
      name: suggestion.name,
      category: suggestion.category as keyof typeof ACTIVITY_CATEGORIES,
      completed: false,
      difficulty: suggestion.difficulty as 'easy' | 'medium' | 'challenging'
    };
    
    setActivities(prev => [...prev, activity]);
    onScheduleActivity?.(activity);
  };

  const completeActivity = (activityId: string) => {
    const updated = activities.map(a => 
      a.id === activityId 
        ? { 
            ...a, 
            completed: true, 
            completedAt: new Date().toISOString(),
            moodBefore: completionData.moodBefore,
            moodAfter: completionData.moodAfter,
            notes: completionData.notes
          }
        : a
    );
    
    const completedActivity = updated.find(a => a.id === activityId);
    setActivities(updated);
    setCompletingActivity(null);
    setCompletionData({ moodBefore: 5, moodAfter: 5, notes: '' });
    
    if (completedActivity) {
      onActivityComplete?.(completedActivity);
    }
  };

  const pendingActivities = activities.filter(a => !a.completed);
  const completedActivities = activities.filter(a => a.completed);
  
  // Calculate mood improvement
  const avgMoodImprovement = completedActivities.length > 0
    ? completedActivities
        .filter(a => a.moodBefore !== undefined && a.moodAfter !== undefined)
        .reduce((sum, a) => sum + (a.moodAfter! - a.moodBefore!), 0) / 
        completedActivities.filter(a => a.moodBefore !== undefined).length
    : 0;

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-500" />
            Behavioural Activation
          </CardTitle>
          <CardDescription>
            When we feel low, we often withdraw from activities, which makes us feel worse. 
            Behavioural activation breaks this cycle by scheduling meaningful activities - 
            even when motivation is low. Action often comes before motivation, not after.
          </CardDescription>
        </CardHeader>
        {completedActivities.length > 0 && (
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>{completedActivities.length} completed</span>
              </div>
              {avgMoodImprovement !== 0 && (
                <div className="flex items-center gap-2">
                  <TrendingUp className={`w-4 h-4 ${avgMoodImprovement > 0 ? 'text-green-500' : 'text-red-500'}`} />
                  <span>
                    {avgMoodImprovement > 0 ? '+' : ''}{avgMoodImprovement.toFixed(1)} avg mood change
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Activity Categories */}
      <div className="space-y-3">
        <h3 className="font-semibold">Activity Categories</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {Object.entries(ACTIVITY_CATEGORIES).map(([key, category]) => (
            <Card 
              key={key}
              className={`cursor-pointer transition-all hover:scale-105 ${category.colour}`}
              onClick={() => setExpandedCategory(expandedCategory === key ? null : key)}
            >
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  {category.icon}
                  <span className="font-medium text-sm">{category.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{category.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Add Suggestions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Quick Add Activities
            </span>
            <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
              <Plus className="w-4 h-4 mr-1" />
              Custom
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_SUGGESTIONS.map((suggestion, i) => {
              const categoryData = ACTIVITY_CATEGORIES[suggestion.category as keyof typeof ACTIVITY_CATEGORIES];
              const alreadyAdded = activities.some(a => a.name === suggestion.name && !a.completed);
              
              return (
                <Badge
                  key={i}
                  variant={alreadyAdded ? 'secondary' : 'outline'}
                  className={`cursor-pointer transition-all ${alreadyAdded ? 'opacity-50' : 'hover:scale-105'}`}
                  onClick={() => !alreadyAdded && addSuggestedActivity(suggestion)}
                >
                  {categoryData.icon}
                  <span className="ml-1">{suggestion.name}</span>
                </Badge>
              );
            })}
          </div>

          {/* Custom Activity Form */}
          <AnimatePresence>
            {showAddForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 p-4 rounded-lg bg-muted/50 border space-y-3">
                  <Input
                    placeholder="Activity name..."
                    value={newActivity.name}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={newActivity.category}
                      onChange={(e) => setNewActivity(prev => ({ 
                        ...prev, 
                        category: e.target.value as keyof typeof ACTIVITY_CATEGORIES 
                      }))}
                    >
                      {Object.entries(ACTIVITY_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.label}</option>
                      ))}
                    </select>
                    <select
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={newActivity.difficulty}
                      onChange={(e) => setNewActivity(prev => ({ 
                        ...prev, 
                        difficulty: e.target.value as 'easy' | 'medium' | 'challenging'
                      }))}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="challenging">Challenging</option>
                    </select>
                  </div>
                  <Input
                    type="datetime-local"
                    value={newActivity.scheduledFor}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, scheduledFor: e.target.value }))}
                  />
                  <Button onClick={addActivity} className="w-full">
                    Add Activity
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Pending Activities */}
      {pendingActivities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Your Activities ({pendingActivities.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingActivities.map(activity => {
              const categoryData = ACTIVITY_CATEGORIES[activity.category];
              const isCompleting = completingActivity === activity.id;
              
              return (
                <div key={activity.id}>
                  <div 
                    className={`flex items-center justify-between p-3 rounded-lg border ${categoryData.colour} ${isCompleting ? 'ring-2 ring-primary' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={false}
                        onCheckedChange={() => setCompletingActivity(activity.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{activity.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {categoryData.icon}
                          <span>{categoryData.label}</span>
                          <Badge variant="outline" className="text-xs">
                            {activity.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {activity.scheduledFor && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {new Date(activity.scheduledFor).toLocaleString('en-GB', { 
                          dateStyle: 'short', 
                          timeStyle: 'short' 
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Completion Form */}
                  <AnimatePresence>
                    {isCompleting && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 p-4 rounded-lg bg-muted/50 border space-y-3">
                          <h4 className="font-medium text-sm">How did it go?</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs font-medium">
                                Mood before: {completionData.moodBefore}/10
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="10"
                                value={completionData.moodBefore}
                                onChange={(e) => setCompletionData(prev => ({ 
                                  ...prev, 
                                  moodBefore: parseInt(e.target.value) 
                                }))}
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium">
                                Mood after: {completionData.moodAfter}/10
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="10"
                                value={completionData.moodAfter}
                                onChange={(e) => setCompletionData(prev => ({ 
                                  ...prev, 
                                  moodAfter: parseInt(e.target.value) 
                                }))}
                                className="w-full"
                              />
                            </div>
                          </div>
                          <Textarea
                            placeholder="Any notes about the experience?"
                            value={completionData.notes}
                            onChange={(e) => setCompletionData(prev => ({ ...prev, notes: e.target.value }))}
                            className="min-h-[60px]"
                          />
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setCompletingActivity(null)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => completeActivity(activity.id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Mark Complete
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Completed Activities */}
      {completedActivities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Completed ({completedActivities.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {completedActivities.slice(0, 5).map(activity => {
              const categoryData = ACTIVITY_CATEGORIES[activity.category];
              const moodChange = activity.moodAfter !== undefined && activity.moodBefore !== undefined
                ? activity.moodAfter - activity.moodBefore
                : null;
              
              return (
                <div 
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-green-500/5 border border-green-500/20"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium text-sm line-through opacity-70">{activity.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.completedAt && new Date(activity.completedAt).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                  </div>
                  {moodChange !== null && (
                    <Badge 
                      variant={moodChange > 0 ? 'default' : moodChange < 0 ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {moodChange > 0 ? '+' : ''}{moodChange} mood
                    </Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Encouragement */}
      {pendingActivities.length === 0 && completedActivities.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-center">
            <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Start Small</h3>
            <p className="text-sm text-muted-foreground">
              Add one easy activity to begin. Remember: the goal isn't to feel motivated first - 
              it's to do the activity and let the mood improvement follow.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
